import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Sparkles, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { api, fmtErr } from "../../lib/api";

const COLORS = ["#0D5C63", "#208E98", "#FF6B5B", "#FF9588", "#4A6168", "#B1E1E6", "#992E23"];
const STAGE_LABELS = { new: "New", contacted: "Contacted", viewing_scheduled: "Viewing Sched.", viewed: "Viewed", application: "Application", leased: "Leased", lost: "Lost" };
const PRESETS = [["7", "Last 7 days"], ["30", "Last 30 days"], ["90", "Last 90 days"], ["all", "All time"]];

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function Analytics() {
  const [preset, setPreset] = useState("30");
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [hood, setHood] = useState("");
  const [plan, setPlan] = useState("");
  const [hoods, setHoods] = useState([]);
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/neighborhoods").then((r) => setHoods(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const params = {};
    if (preset !== "all") { params.from = from; params.to = to; }
    if (hood) params.neighborhood = hood;
    if (plan) params.plan = plan;
    setD(null);
    api.get("/admin/analytics", { params }).then((r) => setD(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [preset, from, to, hood, plan]);

  const pickPreset = (p) => {
    setPreset(p);
    if (p !== "all") { setFrom(isoDaysAgo(Number(p))); setTo(isoDaysAgo(0)); }
  };

  return (
    <div data-testid="analytics-page">
      <h1 className="font-heading font-bold text-2xl tracking-tight">Analytics</h1>
      <p className="text-sm text-muted-foreground mb-6">Live traffic, funnels, and listing performance — plus ask-your-data AI search.</p>

      <AskData />

      {/* Filters */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-4 flex flex-wrap items-center gap-2" data-testid="analytics-filters">
        {PRESETS.map(([v, l]) => (
          <button key={v} data-testid={`preset-${v}`} onClick={() => pickPreset(v)}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${preset === v ? "bg-primary text-primary-foreground" : "border border-border hover:border-primary"}`}>{l}</button>
        ))}
        <span className="text-xs text-muted-foreground ml-2">Custom:</span>
        <input data-testid="filter-from" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }}
          aria-label="From date" className="rounded-lg border border-input bg-background px-2 py-1.5 text-xs" />
        <input data-testid="filter-to" type="date" value={to} onChange={(e) => { setTo(e.target.value); setPreset("custom"); }}
          aria-label="To date" className="rounded-lg border border-input bg-background px-2 py-1.5 text-xs" />
        <select data-testid="filter-neighborhood" value={hood} onChange={(e) => setHood(e.target.value)} aria-label="Neighbourhood filter"
          className="rounded-lg border border-input bg-background px-2 py-1.5 text-xs">
          <option value="">All neighbourhoods</option>
          {hoods.map((h) => <option key={h.slug} value={h.name}>{h.name}</option>)}
        </select>
        <select data-testid="filter-plan" value={plan} onChange={(e) => setPlan(e.target.value)} aria-label="Plan filter"
          className="rounded-lg border border-input bg-background px-2 py-1.5 text-xs">
          <option value="">All plans</option>
          <option value="per_listing">Per Listing</option><option value="starter">Starter</option>
          <option value="growth">Growth</option><option value="unlimited">Unlimited</option>
        </select>
      </div>

      {!d ? <p className="mt-8 text-muted-foreground">Loading…</p> : (
        <>
          {d.leads_total === 0 && d.traffic.visits === 0 && (
            <div data-testid="analytics-empty" className="mt-6 rounded-2xl border border-dashed border-border bg-card p-5 text-sm text-muted-foreground">
              Data will appear as activity comes in — visits, chats, leads, viewings and listings all feed these charts live.
            </div>
          )}

          {/* Traffic */}
          <h2 className="mt-8 font-heading font-semibold text-lg">Traffic</h2>
          <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Site visits" value={d.traffic.visits} tid="stat-visits" />
            <Stat label="Unique visitors" value={d.traffic.unique_visitors} tid="stat-visitors" />
            <Stat label="CTA clicks" value={d.traffic.clicks} tid="stat-clicks" />
            <Stat label="Active listings" value={d.listings.active} tid="stat-active" />
          </div>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-5">
              <h3 className="text-sm font-semibold">Visits trend ({d.range.from} → {d.range.to})</h3>
              <div className="h-48 mt-3">
                <ResponsiveContainer>
                  <AreaChart data={d.traffic.trend}>
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip /><Area type="monotone" dataKey="count" stroke="#0D5C63" fill="#B1E1E6" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="text-sm font-semibold">Top pages</h3>
              <ul className="mt-3 space-y-1.5 text-sm">
                {d.traffic.top_pages.map(([p, c]) => <li key={p} className="flex justify-between gap-2"><span className="truncate font-mono text-xs">{p}</span><span className="font-semibold">{c}</span></li>)}
                {!d.traffic.top_pages.length && <li className="text-xs text-muted-foreground">No visits in this range yet.</li>}
              </ul>
              {d.traffic.sources.length > 0 && (
                <>
                  <h3 className="mt-4 text-sm font-semibold">Sources</h3>
                  <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                    {d.traffic.sources.map(([s, c]) => <li key={s} className="flex justify-between gap-2"><span className="truncate">{s}</span><span>{c}</span></li>)}
                  </ul>
                </>
              )}
            </div>
          </div>

          {/* Chat funnel */}
          <h2 className="mt-8 font-heading font-semibold text-lg">Chat funnel</h2>
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Stat label="Chats started" value={d.funnel.started} tid="stat-chats" />
            <Stat label="Chats completed" value={d.funnel.completed} tid="stat-completed" />
            <Stat label="Completion rate" value={d.funnel.completion_rate != null ? `${d.funnel.completion_rate}%` : "—"} tid="stat-completion" />
          </div>
          {Object.keys(d.funnel.steps).length > 0 && (
            <div className="mt-4 rounded-2xl border border-border bg-card p-5">
              <h3 className="text-sm font-semibold">Drop-off by question step</h3>
              <div className="h-44 mt-3">
                <ResponsiveContainer>
                  <BarChart data={Object.entries(d.funnel.steps).map(([s, c]) => ({ step: `Q${Number(s) + 1}`, count: c }))}>
                    <XAxis dataKey="step" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip /><Bar dataKey="count" fill="#208E98" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Conversion funnel */}
          <h2 className="mt-8 font-heading font-semibold text-lg">Conversion funnel</h2>
          <div className="mt-3 rounded-2xl border border-border bg-card p-5" data-testid="conversion-funnel">
            <div className="space-y-2">
              {d.conversion.stages.map((s, i) => {
                const max = d.conversion.stages[0].count || 1;
                const rate = i > 0 ? d.conversion.rates[i - 1] : null;
                return (
                  <div key={s.key} data-testid={`stage-${s.key}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span>{s.label}</span>
                      <span className="font-semibold">{s.count}{rate && rate.rate != null && <span className="ml-2 text-xs font-normal text-primary">↓ {rate.rate}%</span>}</span>
                    </div>
                    <div className="mt-1 h-2.5 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${Math.max((s.count / max) * 100, s.count ? 3 : 0)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Listings */}
          <h2 className="mt-8 font-heading font-semibold text-lg">Listings performance</h2>
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="text-sm font-semibold">Views per listing</h3>
              <table className="mt-3 w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground"><th className="pb-2">Listing</th><th className="pb-2">Views</th></tr></thead>
                <tbody>
                  {d.listings.views.map((v) => (
                    <tr key={v.listing_id} className="border-t border-border/60"><td className="py-1.5 text-xs">{v.address} <span className="text-muted-foreground">· {v.neighborhood}</span></td><td className="font-semibold">{v.views}</td></tr>
                  ))}
                  {!d.listings.views.length && <tr><td colSpan={2} className="py-4 text-xs text-muted-foreground">No listing views in this range.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="text-sm font-semibold">Leads per listing</h3>
              <table className="mt-3 w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground"><th className="pb-2">Listing</th><th className="pb-2">Leads</th></tr></thead>
                <tbody>
                  {d.listings.leads_per_listing.map((v) => (
                    <tr key={v.listing_id} className="border-t border-border/60"><td className="py-1.5 text-xs">{v.address} <span className="text-muted-foreground">· {v.neighborhood}</span></td><td className="font-semibold">{v.leads}</td></tr>
                  ))}
                  {!d.listings.leads_per_listing.length && <tr><td colSpan={2} className="py-4 text-xs text-muted-foreground">No viewing requests in this range.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {/* Blog performance */}
          <h2 className="mt-8 font-heading font-semibold text-lg">Blog performance</h2>
          <div className="mt-3 rounded-2xl border border-border bg-card p-5" data-testid="blog-perf-section">
            <p className="text-xs text-muted-foreground">Blog index visits in range: <strong>{d.blog.index_views}</strong> · CTR = card clicks ÷ index visits</p>
            <div className="overflow-x-auto">
              <table className="mt-3 w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground">
                  <th className="pb-2 pr-4">Post</th><th className="pb-2 pr-4">Views</th><th className="pb-2 pr-4">Unique</th><th className="pb-2 pr-4">Card clicks</th><th className="pb-2 pr-4">CTR</th><th className="pb-2">Views over time</th>
                </tr></thead>
                <tbody>
                  {d.blog.posts.map((b) => (
                    <tr key={b.slug} data-testid={`blog-perf-${b.slug}`} className="border-t border-border/60">
                      <td className="py-2 pr-4 text-xs max-w-[220px]">
                        <a href={`/blog/${b.slug}`} target="_blank" rel="noreferrer" className="hover:text-primary font-semibold">{b.title}</a>
                        <span className="block text-muted-foreground font-mono">/blog/{b.slug}</span>
                      </td>
                      <td className="pr-4 font-semibold">{b.views}</td>
                      <td className="pr-4">{b.unique}</td>
                      <td className="pr-4">{b.card_clicks}</td>
                      <td className="pr-4">{b.ctr != null ? `${b.ctr}%` : "—"}</td>
                      <td className="py-1">
                        {b.trend.length > 0 ? (
                          <div className="h-10 w-28"><ResponsiveContainer><AreaChart data={b.trend}><Area type="monotone" dataKey="count" stroke="#208E98" fill="#B1E1E6" strokeWidth={1.5} /></AreaChart></ResponsiveContainer></div>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  ))}
                  {!d.blog.posts.length && <tr><td colSpan={6} className="py-4 text-xs text-muted-foreground">No blog posts yet — publish one in Content → Blog posts.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {/* Poster QR scans */}
          <h2 className="mt-8 font-heading font-semibold text-lg">Poster QR scans</h2>
          <div className="mt-3 rounded-2xl border border-border bg-card p-5" data-testid="poster-scans-section">
            <p className="text-xs text-muted-foreground">Total scans in range: <strong>{d.posters.total}</strong> · each poster's QR points to tendlyrentals.com/p/<strong>&lt;poster-name&gt;</strong> and redirects to the homepage</p>
            {d.posters.items.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="mt-3 w-full text-sm">
                  <thead><tr className="text-left text-xs text-muted-foreground">
                    <th className="pb-2 pr-4">Poster</th><th className="pb-2 pr-4">Scans</th><th className="pb-2 pr-4">Unique</th><th className="pb-2">Scans over time</th>
                  </tr></thead>
                  <tbody>
                    {d.posters.items.map((p) => (
                      <tr key={p.code} data-testid={`poster-scan-${p.code}`} className="border-t border-border/60">
                        <td className="py-2 pr-4 font-mono text-xs font-semibold">/p/{p.code}</td>
                        <td className="pr-4 font-semibold">{p.scans}</td>
                        <td className="pr-4">{p.unique}</td>
                        <td className="py-1">
                          <div className="h-10 w-28"><ResponsiveContainer><AreaChart data={p.trend}><Area type="monotone" dataKey="count" stroke="#FF6B5B" fill="#FFE3DF" strokeWidth={1.5} /></AreaChart></ResponsiveContainer></div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-3 text-xs text-muted-foreground">No scans yet in this range. Scans appear here the moment someone opens a poster QR link.</p>
            )}
          </div>

          {/* Legacy sections */}
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="font-heading font-semibold text-lg">Leads by stage</h2>
              <div className="h-56 mt-4">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={Object.entries(d.leads_by_status).map(([k, v]) => ({ name: STAGE_LABELS[k] || k, value: v }))} dataKey="value" nameKey="name" outerRadius={80} label>
                      {Object.keys(d.leads_by_status).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip /><Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="font-heading font-semibold text-lg">Most-requested neighbourhoods</h2>
              <div className="h-56 mt-4">
                <ResponsiveContainer>
                  <BarChart data={d.demand_neighborhoods.map(([name, count]) => ({ name, count }))} layout="vertical" margin={{ left: 40 }}>
                    <XAxis type="number" allowDecimals={false} /><YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
                    <Tooltip /><Bar dataKey="count" fill="#0D5C63" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="font-heading font-semibold text-lg">Inventory</h2>
              <dl className="mt-4 space-y-2 text-sm">
                <Row k="Active listings" v={d.inventory.total} />
                <Row k="Avg quality score" v={d.inventory.avg_quality} />
              </dl>
              <h3 className="mt-4 text-xs uppercase tracking-wider font-semibold text-muted-foreground">By source</h3>
              <dl className="mt-2 space-y-1.5 text-sm">
                {Object.entries(d.inventory.by_source).map(([k, v]) => <Row key={k} k={k} v={v} />)}
              </dl>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="font-heading font-semibold text-lg">Outreach</h2>
              <dl className="mt-4 space-y-2 text-sm">
                <Row k="Drafts awaiting review" v={d.outreach.draft || 0} />
                <Row k="Sent" v={d.outreach.sent || 0} />
                <Row k="Replied" v={d.outreach.replied || 0} />
                <Row k="Referral confirmed" v={d.outreach.referral_confirmed || 0} />
                <Row k="Declined" v={d.outreach.declined || 0} />
              </dl>
              <h3 className="mt-4 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Budget demand</h3>
              <dl className="mt-2 space-y-1.5 text-sm">
                {Object.entries(d.demand_budget).map(([k, v]) => <Row key={k} k={k} v={v} />)}
              </dl>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="font-heading font-semibold text-lg">Agent performance</h2>
              <table className="mt-4 w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground"><th className="pb-2">Agent</th><th className="pb-2">Leads</th><th className="pb-2">Won</th></tr></thead>
                <tbody>
                  {d.agents.map((a) => (
                    <tr key={a.name} className="border-t border-border/60"><td className="py-2">{a.name}</td><td>{a.leads}</td><td>{a.won}</td></tr>
                  ))}
                </tbody>
              </table>
              <h3 className="mt-4 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Beds demand</h3>
              <dl className="mt-2 space-y-1.5 text-sm">
                {Object.entries(d.demand_beds).map(([k, v]) => <Row key={k} k={k} v={v} />)}
              </dl>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AskData() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [showRecords, setShowRecords] = useState(false);

  const loadRecent = () => api.get("/admin/analytics/ask/recent").then((r) => setRecent(r.data)).catch(() => {});
  useEffect(() => { loadRecent(); }, []);

  const ask = async (question) => {
    const query = (question || q).trim();
    if (!query) return;
    setLoading(true);
    setResult(null);
    setShowRecords(false);
    try {
      const r = await api.post("/admin/analytics/ask", { question: query });
      setResult({ question: query, ...r.data });
      loadRecent();
    } catch (e) { toast.error(fmtErr(e)); }
    setLoading(false);
  };

  return (
    <section data-testid="ask-data" className="rounded-2xl border border-primary/30 bg-teal-50/50 dark:bg-teal-900/20 p-5">
      <h2 className="font-heading font-semibold text-lg flex items-center gap-2"><Sparkles className="w-5 h-5 text-accent" /> Ask your data</h2>
      <div className="mt-3 flex gap-2">
        <input data-testid="ask-input" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder='e.g. "What is the chat completion rate over the last 30 days?"'
          aria-label="Ask an analytics question"
          className="flex-1 rounded-xl border border-input bg-background px-4 py-2.5 text-sm" />
        <button data-testid="ask-submit" onClick={() => ask()} disabled={loading}
          className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Ask
        </button>
      </div>
      {recent.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {recent.slice(0, 6).map((r) => (
            <button key={r.id} data-testid={`recent-q-${r.id}`} onClick={() => { setQ(r.question); ask(r.question); }}
              className="rounded-full border border-border bg-card px-3 py-1 text-xs hover:border-primary transition-colors">{r.question}</button>
          ))}
        </div>
      )}
      {result?.clarification && (
        <div data-testid="ask-clarification" className="mt-4 rounded-xl bg-card border border-border p-4 text-sm">
          <span className="font-semibold">One quick clarification: </span>{result.clarification}
        </div>
      )}
      {result && !result.clarification && (
        <div data-testid="ask-answer" className="mt-4 rounded-xl bg-card border border-border p-5">
          <p className="text-xs text-muted-foreground">{result.question}</p>
          <p data-testid="ask-answer-value" className="mt-1 font-heading font-bold text-3xl tracking-tight">{result.answer}</p>
          {result.numerator != null && <p className="text-sm text-muted-foreground">{result.numerator} of {result.denominator} records</p>}
          {result.groups && (
            <ul className="mt-2 text-sm space-y-1">
              {result.groups.slice(0, 6).map((g) => <li key={g.group} className="flex justify-between max-w-xs"><span>{g.group}</span><span className="font-semibold">{g.count}</span></li>)}
            </ul>
          )}
          <p data-testid="ask-explanation" className="mt-2 text-xs text-muted-foreground leading-relaxed">{result.explanation}</p>
          {result.records && result.records.length > 0 && (
            <div className="mt-3">
              <button data-testid="ask-records-toggle" onClick={() => setShowRecords(!showRecords)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
                {showRecords ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {showRecords ? "Hide" : "Show"} the {result.records.length} underlying record{result.records.length !== 1 ? "s" : ""}{result.truncated ? " (first 25)" : ""}
              </button>
              {showRecords && (
                <div className="mt-2 max-h-64 overflow-auto rounded-lg border border-border">
                  <table className="w-full text-xs">
                    <tbody>
                      {result.records.map((r, i) => (
                        <tr key={i} className="border-b border-border/50 last:border-0">
                          <td className="px-3 py-2 font-mono whitespace-pre-wrap">{Object.entries(r).filter(([, v]) => v != null && v !== "").map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`).join("  ·  ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          {result.records && result.records.length === 0 && result.count === 0 && (
            <p className="mt-2 text-xs text-muted-foreground">No matching records — this is a true zero, not an estimate.</p>
          )}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, sub, tid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={tid}>
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
      <p className="mt-2 font-heading font-bold text-3xl tracking-tight">{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between"><dt className="text-muted-foreground">{k}</dt><dd className="font-semibold">{v}</dd></div>;
}
